import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';

import { MAX_DURATION_SEC } from '../../components/RecorderTimeLimit';

import { getVideoDuration } from './getVideo';

type VideoValid = {
	isVideoValid: (file: File) => Promise<{ isValid: boolean; message: string; duration?: number }>;
};

/**
 * Where a measured duration came from. `probe` means it was read off the blob itself and is
 * authoritative; `wall-clock` means the blob reported no usable duration and the number is the
 * recorder's own bookkeeping; `unknown` means neither was available. The distinction matters when
 * reporting a rejection — see `create_video_duration_rejected` in TRACKING_EVENTS.md.
 */
type DurationSource = 'probe' | 'wall-clock' | 'unknown';

type DurationVerdict = {
	isValid: boolean;
	message: string;
	duration?: number;
	durationSource?: DurationSource;
};

type VideoDurationValid = {
	isVideoDurationValid: (video: Blob, fallbackDurationSec?: number) => Promise<DurationVerdict>;
};

const SUPPORTED_FORMATS = ['video/webm', 'video/mp4', 'video/quicktime'];
// Encoders routinely overshoot the requested cutoff by a fraction of a second, so validation allows a
// little more than the recorder aims for.
const DURATION_TOLERANCE_SEC = 3;
const MAX_DURATION_UPLOAD_SEC = MAX_DURATION_SEC + DURATION_TOLERANCE_SEC;
const MEGABYTE = 1024 ** 2;

export function useIsVideoValid(): VideoValid {
	const {
		data: {
			featureConfigs: { campaignSupporterVoicesSettings },
		},
	} = usePetitionActionsPageData();
	const { translate } = useI18n();

	const MIN_SIZE_VIDEO = (campaignSupporterVoicesSettings.minVideoSize || 0.04) * MEGABYTE;
	const MAX_SIZE_VIDEO = (campaignSupporterVoicesSettings.maxVideoSize || 20) * MEGABYTE;

	const isSizeAndFormatValid = useCallback(
		(file: File) => {
			if (!SUPPORTED_FORMATS.includes(file.type)) {
				return translate('corgi.petition_actions.supporter_voice_video.choose_file.errors.file_type', {
					fileType: file.type || 'unknown',
				});
			}

			if (file.size < MIN_SIZE_VIDEO) {
				return translate('corgi.petition_actions.supporter_voice_video.choose_file.errors.size_min');
			}

			if (file.size >= MAX_SIZE_VIDEO) {
				return translate('corgi.petition_actions.supporter_voice_video.choose_file.errors.size_max');
			}

			return null;
		},
		[MIN_SIZE_VIDEO, MAX_SIZE_VIDEO, translate],
	);

	const isVideoValid = useCallback(
		async (file: File): Promise<{ isValid: boolean; message: string; duration?: number }> => {
			const message = isSizeAndFormatValid(file);
			if (message) return { isValid: false, message };

			const { validDuration, duration } = await isFileDurationValid(file);
			if (!validDuration) {
				return {
					isValid: false,
					message: translate('corgi.petition_actions.supporter_voice_video.choose_file.errors.duration_max'),
					duration,
				};
			}

			return { isValid: true, message: '' };
		},
		[isSizeAndFormatValid, translate],
	);

	return { isVideoValid };
}

/**
 * Duration-only check for blobs produced by the recorder.
 *
 * The recorder's own cutoff is the primary limit; this is the backstop for the case where it fails. The
 * full `useIsVideoValid` cannot be reused here: recorder blobs carry a mime type with a codecs parameter
 * (`video/webm;codecs="vp8,opus"`) that no entry in `SUPPORTED_FORMATS` matches, and their size is
 * already constrained by the upload restrictions.
 *
 * `fallbackDurationSec` is the wall-clock time the recorder measured, used when the blob's own duration
 * cannot be read. A duration that stays unknown is treated as valid rather than blocking submission.
 */
export function useIsVideoDurationValid(): VideoDurationValid {
	const { translate } = useI18n();

	const isVideoDurationValid = useCallback(
		async (video: Blob, fallbackDurationSec?: number): Promise<DurationVerdict> => {
			const { duration, durationSource } = await measureDuration(video, fallbackDurationSec);

			if (duration !== undefined && !isValidDuration(duration).validDuration) {
				return {
					isValid: false,
					message: translate('corgi.petition_actions.supporter_voice_video.record.errors.duration_max'),
					duration,
					durationSource,
				};
			}

			return { isValid: true, message: '', duration, durationSource };
		},
		[translate],
	);

	return { isVideoDurationValid };
}

function isValidDuration(duration: number) {
	if (duration <= MAX_DURATION_UPLOAD_SEC) {
		return { validDuration: true };
	}
	return { validDuration: false, duration };
}

async function isFileDurationValid(file: File): Promise<{ validDuration: boolean; duration?: number }> {
	try {
		const videoElement = document.createElement('video');
		const duration = await getVideoDuration(file, videoElement);
		if (!Number.isFinite(duration)) {
			return { validDuration: false };
		}
		return isValidDuration(duration);
	} catch (error) {
		return { validDuration: false };
	}
}

async function measureDuration(
	video: Blob,
	fallbackDurationSec?: number,
): Promise<{ duration?: number; durationSource: DurationSource }> {
	try {
		const videoElement = document.createElement('video');
		const duration = await getVideoDuration(video, videoElement);
		if (Number.isFinite(duration)) {
			return { duration, durationSource: 'probe' };
		}
	} catch {
		// Falls back to the recorder's own measurement below.
	}

	if (fallbackDurationSec === undefined) {
		// Reported by the caller: a duration nobody can measure means the backstop is not actually
		// guarding anything, and we would otherwise never hear about it.
		return { durationSource: 'unknown' };
	}

	return { duration: fallbackDurationSec, durationSource: 'wall-clock' };
}
